// Tiny typed fetch wrapper. No retries, no caching — this is an MVP.
// Base URL comes from VITE_API_BASE_URL, defaulting to same-origin so the
// Vite proxy / Nginx can route /api to the backend transparently.

import type { ApiError } from "./types";

const API_BASE: string = (import.meta.env.VITE_API_BASE_URL ?? "").replace(
  /\/$/,
  "",
);

export class HttpError extends Error {
  status: number;
  detail: ApiError["detail"];

  constructor(status: number, detail: ApiError["detail"], message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
  }

  /** Best-effort human-readable single-line message. */
  toUserMessage(): string {
    if (typeof this.detail === "string") return this.detail;
    if (Array.isArray(this.detail) && this.detail.length > 0) {
      return this.detail.map((d) => d.msg).join("; ");
    }
    return this.message;
  }
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export interface RequestOptions {
  method?: string;
  json?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const init: RequestInit = {
    method: options.method ?? "GET",
    signal: options.signal,
    credentials: options.credentials,
    headers: {
      Accept: "application/json",
      ...(options.headers ?? {}),
    },
  };
  if (options.json !== undefined) {
    init.body = JSON.stringify(options.json);
    init.headers = {
      ...init.headers,
      "Content-Type": "application/json",
    };
  }

  const response = await fetch(url, init);
  const body = await parseBody(response);

  if (!response.ok) {
    const detail =
      (body as ApiError | null)?.detail ??
      (typeof body === "string" ? body : `HTTP ${response.status}`);
    throw new HttpError(response.status, detail);
  }

  return body as T;
}

export function publicUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}
